/**
 * Brain → Finance → Productividad de Content: el modelo de cálculo, puro.
 *
 * Toma las filas que el backend lee de BigQuery (snapshots de Replit en
 * `neondb_hourAllocator`, parámetros del modelo en `Capacity_CP_Model_US`, revenue y
 * margen en `Capacity_Process.Capacity_Final`, medianas de Partnerships en
 * `partnerships_forecast`) y devuelve el digest en markdown por sección que cita el
 * área Finance del Brain. Sin I/O: lo usa `backend/src/lib/content-productivity-digest.ts`
 * y lo testea `content-productivity.test.ts`.
 *
 * Reglas (borrador v2 del system prompt de Productividad de Content, 21/10/2026):
 *  - PI = need ÷ FTE real = 1 / (1 + gap_pct). PI 1 = estándar; > 1 se usó menos gente que el
 *    estándar; < 1 se usó más. Semáforo ±20 % sobre gap_pct. PI alto NO es "bueno" sin más:
 *    es riesgo de delivery. Un gap de −1 (−100 %) = hay assets y nadie asignado.
 *  - El estándar es uniforme: nunca se sugiere bajarlo para un cliente. Los desvíos se explican.
 *  - Los snapshots no se recalculan: hasta septiembre 2026 son V1; el primer V2 es el cierre de
 *    octubre. Comparar meses de modelos distintos no es 100 % comparable.
 *  - Confidencialidad: nunca costo por persona ni por rol (acá directamente no entra ningún dato
 *    por persona); margen solo a nivel cliente y solo si el cliente tiene ≥ 2 roles con costo;
 *    costo 0 o GM ≥ 95 % = costo de creadores sin cargar ("margen no confiable"), no rentabilidad.
 *  - Open capacity = FTE en SLACK - *, PTO/OOO, TRAININGS u OTHER PROJECTS, por líder; NOT
 *    ALLOCATED aparte; CAPACITY - * son bolsas de Partnerships (no son open capacity).
 *  - El coeficiente ×0,6 de Andinos en Partnerships multiplica el NEED, no los packs, y no vive en
 *    ninguna tabla: se cita como regla vigente del modelo.
 */

import { fmtK, fmtPct0, monthNameEs } from './brain-format';
import { pctDelta } from './finance-pnl';
import { mdFields, mdTable } from './md-table';

/* ───────────────────────── Filas de entrada (lo que lee el backend) ───────────────────────── */

export type ContentModel = 'CP' | 'CS';

/** Una fila de cp_capacity_snapshots / cs_capacity_snapshots (mes cerrado). */
export interface SnapshotRow {
  model: ContentModel;
  client: string;
  /** client_extras.geo: SoCo, Andinos, Mexico, Brazil, MELI, KC, US…; null = sin geo. */
  pod: string | null;
  year: number;
  month: number;
  /** FTE reales en equivalentes Senior Analyst. */
  fte: number;
  /** gap_q = FTE real − need (SA eq). null = sin need (sin assets cargados). */
  gapQ: number | null;
  /** gap_q ÷ need. null = sin need. −1 = assets sin nadie asignado. */
  gapPct: number | null;
  modelVersion: string;
  hasEstimado: boolean;
}

/** Una fila de cp_forecast_snapshots (proyección congelada al cierre `actual`). */
export interface ForecastRow extends Omit<SnapshotRow, 'model' | 'year' | 'month' | 'hasEstimado'> {
  actualYear: number;
  actualMonth: number;
  forecastYear: number;
  forecastMonth: number;
}

/** allocations × clients × team_members(manager) del mes, ya sumadas por líder y cliente. */
export interface AllocationRow {
  /** Nombre del líder (managers.name); null = sin manager. */
  lead: string | null;
  client: string;
  fte: number;
}

/** Capacity_Final (Actual, PracticeName = 'Content') del mes, agrupado por cliente. */
export interface MarginRow {
  client: string;
  revenue: number;
  cost: number;
  /** Roles distintos con costo > 0: con menos de 2 no se muestra el margen (se deduciría un sueldo). */
  rolesWithCost: number;
}

/** Parámetros del modelo CP (Capacity_CP_Model_US): horas fijas por tier, horas por asset, horas efectivas. */
export interface ModelParamRow {
  kind: 'tier' | 'asset' | 'time';
  modelVersion: string;
  name: string;
  value: number | null;
  validFrom: string | null;
  validTo: string | null;
}

/** partnership_package_std sin cost_avg (nunca se lee): FTE estándar por partner y pack. */
export interface PackStdRow {
  partner: string;
  pack: string;
  lead: number | null;
  operator: number | null;
  cs: number | null;
  total: number | null;
}

/** Mediana de días de entrega de proyectos cerrados de Partnerships, por región o por plataforma. */
export interface MedianRow {
  group: string;
  projects: number;
  medianDays: number;
}

/** Completitud de carga del mes por líder directo (conteos, sin nombres de personas). */
export interface LeadLoadRow {
  lead: string | null;
  people: number;
  /** Suman exactamente 100 %. */
  ok: number;
  noLoad: number;
  incomplete: number;
  exceeded: number;
}

/** Assets del mes por tipo (CP), por modo × interno/crowd (CS) o packs por partner (Partnerships). */
export interface AssetTypeRow {
  source: 'CP' | 'CS' | 'Partnerships';
  label: string;
  isForecast: boolean;
  qty: number;
  /** Mismo corte del mes anterior (solo real); null si no aplica. */
  prevQty: number | null;
  clients: number;
}

/** Assets reales de CP del mes por cliente (para assets por FTE). */
export interface ClientAssetsRow {
  client: string;
  qty: number;
}

/** Capacity_CP_Model_US.client_name_crosswalk vigente: nombre en Replit → nombre(s) en Capacity_Final. */
export interface CrosswalkRow {
  replit: string;
  capacity: string;
}

export interface ContentProductivityInput {
  /** YYYY-MM-DD del día que se generó. */
  today: string;
  /** Último mes cerrado con snapshot CP (1-12) y su año. */
  year: number;
  month: number;
  snapshots: SnapshotRow[];
  forecasts: ForecastRow[];
  allocations: AllocationRow[];
  margins: MarginRow[];
  params: ModelParamRow[];
  packs: PackStdRow[];
  mediansByRegion: MedianRow[];
  mediansByPlatform: MedianRow[];
  /** Bloques A y C del skill original (opcionales: un digest viejo no los trae). */
  leadLoad?: LeadLoadRow[];
  assets?: AssetTypeRow[];
  clientAssets?: ClientAssetsRow[];
  locksCount?: number | null;
  /** Clientes de Content con horas del mes y sin assets ni packs cargados. */
  hoursWithoutAssets?: string[];
  crosswalk?: CrosswalkRow[];
}

/* ───────────────────────── Secciones del digest ───────────────────────── */

export type ContentSection = 'summary' | 'model' | 'clients' | 'pods' | 'openCapacity' | 'matrix' | 'partnerships' | 'forecast' | 'load' | 'assets' | 'assumptions';

export const CONTENT_SECTION_TITLE: Record<ContentSection, string> = {
  summary: 'Productividad de Content — resumen',
  model: 'Modelo de capacity de Content (PI, V1 → V2)',
  clients: 'PI por cliente (CP y CS)',
  pods: 'PI por pod',
  openCapacity: 'Open capacity por líder',
  matrix: 'Matriz margen × productividad por cliente',
  partnerships: 'Partnerships: estándar por pack y medianas de entrega',
  forecast: 'Forecast de capacity (proyectado)',
  load: 'Completitud de carga por líder',
  assets: 'Assets producidos (CP, CS, packs)',
  assumptions: 'Reglas de lectura, confidencialidad y datos faltantes',
};

export type ContentProductivitySections = Record<ContentSection, string>;

/* ───────────────────────── Cálculo ───────────────────────── */

/** Filas de los snapshots y de allocations que no son clientes. */
export function isNonClient(name: string): boolean {
  const n = name.toUpperCase().trim();
  // "OM OTHER PROJECTS" (Owned Media) es la bolsa de proyectos internos de esa solución (sync del Org Service en prod, 26/10/2026).
  return n.startsWith('SLACK - ') || n.startsWith('CAPACITY - ') || n.endsWith(' OTHER PROJECTS') || ['PTO/OOO', 'TRAININGS', 'OTHER PROJECTS', 'NOT ALLOCATED'].includes(n);
}

/** "v1_2026H1_calibrado" → "V1"; "v2_2026Q4" → "V2"; otra cosa → tal cual. */
export function modelLabel(version: string): string {
  const m = /^v(\d+)/i.exec(version.trim());
  return m ? `V${m[1]}` : version;
}

export type PiStatus = 'sin_need' | 'sin_gente' | 'sobre_dotado' | 'sub_dotado' | 'estandar';

export const PI_STATUS_LABEL: Record<PiStatus, string> = {
  sin_need: 'sin need (sin assets cargados)',
  sin_gente: 'assets sin nadie asignado',
  sobre_dotado: 'sobre-dotado / baja productividad',
  sub_dotado: 'sub-dotado (riesgo de delivery)',
  estandar: 'dentro de estándar',
};

/** Semáforo del modelo: ±20 % sobre gap_pct. */
export function piStatus(gapPct: number | null): PiStatus {
  if (gapPct === null || !Number.isFinite(gapPct)) return 'sin_need';
  if (gapPct <= -0.999) return 'sin_gente';
  if (gapPct > 0.2) return 'sobre_dotado';
  if (gapPct < -0.2) return 'sub_dotado';
  return 'estandar';
}

/** PI = need ÷ FTE real, con need = fte − gap_q. null si no hay need o no hay FTE. */
export function piOf(fte: number, gapQ: number | null): number | null {
  if (gapQ === null || !Number.isFinite(gapQ) || !(fte > 0)) return null;
  return (fte - gapQ) / fte;
}

export interface ClientPi {
  model: ContentModel;
  client: string;
  pod: string;
  fte: number;
  need: number | null;
  pi: number | null;
  gapPct: number | null;
  status: PiStatus;
  hasEstimado: boolean;
  modelVersion: string;
}

/** PI por cliente del mes: sin los no-clientes, ordenado de peor a mejor PI (los sin need al final). */
export function computeClientPi(rows: SnapshotRow[]): ClientPi[] {
  return rows
    .filter((r) => r.fte > 0 && !isNonClient(r.client))
    .map((r) => ({
      model: r.model,
      client: r.client,
      pod: r.pod ?? '(sin geo)',
      fte: r.fte,
      need: r.gapQ === null ? null : r.fte - r.gapQ,
      pi: piOf(r.fte, r.gapQ),
      gapPct: r.gapPct,
      status: piStatus(r.gapPct),
      hasEstimado: r.hasEstimado,
      modelVersion: r.modelVersion,
    }))
    .sort((a, b) => (a.pi ?? Infinity) - (b.pi ?? Infinity) || a.client.localeCompare(b.client));
}

export interface PodPi {
  pod: string;
  clients: number;
  need: number;
  fte: number;
  /** Σneed ÷ ΣFTE (ponderado), no promedio de PI. */
  pi: number | null;
  sobreDotados: number;
  subDotados: number;
}

/** PI ponderado por pod (solo clientes con need). */
export function computePodPi(rows: SnapshotRow[], model: ContentModel): PodPi[] {
  const acc = new Map<string, PodPi>();
  for (const r of rows) {
    if (r.model !== model || !(r.fte > 0) || r.gapQ === null || isNonClient(r.client)) continue;
    const pod = r.pod ?? '(sin geo)';
    const e = acc.get(pod) ?? { pod, clients: 0, need: 0, fte: 0, pi: null, sobreDotados: 0, subDotados: 0 };
    e.clients += 1;
    e.need += r.fte - r.gapQ;
    e.fte += r.fte;
    const st = piStatus(r.gapPct);
    if (st === 'sobre_dotado') e.sobreDotados += 1;
    if (st === 'sub_dotado') e.subDotados += 1;
    acc.set(pod, e);
  }
  return [...acc.values()].map((e) => ({ ...e, pi: e.fte > 0 ? e.need / e.fte : null })).sort((a, b) => b.fte - a.fte);
}

export interface LeadOpenCapacity {
  lead: string;
  fteTotal: number;
  slack: number;
  pto: number;
  trainings: number;
  otherProjects: number;
  openCapacity: number;
  openPct: number | null;
  notAllocated: number;
}

export interface OpenCapacityView {
  leads: LeadOpenCapacity[];
  totals: { slack: number; pto: number; trainings: number; otherProjects: number; notAllocated: number; openCapacity: number; fteTotal: number };
  /** CAPACITY - *: bolsas de Partnerships, se informan aparte, no son open capacity. */
  capacityPools: number;
}

const OPEN_CATEGORY = (client: string): 'slack' | 'pto' | 'trainings' | 'otherProjects' | 'notAllocated' | 'capacityPool' | null => {
  const n = client.toUpperCase().trim();
  if (n.startsWith('SLACK - ')) return 'slack';
  if (n === 'PTO/OOO') return 'pto';
  if (n === 'TRAININGS') return 'trainings';
  if (n === 'OTHER PROJECTS') return 'otherProjects';
  if (n === 'NOT ALLOCATED') return 'notAllocated';
  if (n.startsWith('CAPACITY - ')) return 'capacityPool';
  return null;
};

/** Open capacity por líder directo, con el desglose por categoría; NOT ALLOCATED aparte. */
export function computeOpenCapacity(rows: AllocationRow[]): OpenCapacityView {
  const acc = new Map<string, LeadOpenCapacity>();
  const totals = { slack: 0, pto: 0, trainings: 0, otherProjects: 0, notAllocated: 0, openCapacity: 0, fteTotal: 0 };
  let capacityPools = 0;
  for (const r of rows) {
    const cat = OPEN_CATEGORY(r.client);
    if (cat === 'capacityPool') {
      capacityPools += r.fte;
      continue;
    }
    const lead = r.lead ?? '(sin manager)';
    const e = acc.get(lead) ?? { lead, fteTotal: 0, slack: 0, pto: 0, trainings: 0, otherProjects: 0, openCapacity: 0, openPct: null, notAllocated: 0 };
    e.fteTotal += r.fte;
    totals.fteTotal += r.fte;
    if (cat && cat !== 'notAllocated') {
      e[cat] += r.fte;
      e.openCapacity += r.fte;
      totals[cat] += r.fte;
      totals.openCapacity += r.fte;
    } else if (cat === 'notAllocated') {
      e.notAllocated += r.fte;
      totals.notAllocated += r.fte;
    }
    acc.set(lead, e);
  }
  const leads = [...acc.values()]
    .filter((e) => e.fteTotal > 0)
    .map((e) => ({ ...e, openPct: e.fteTotal > 0 ? e.openCapacity / e.fteTotal : null }))
    .sort((a, b) => b.openCapacity - a.openCapacity || a.lead.localeCompare(b.lead));
  return { leads, totals, capacityPools };
}

/** "KIMBERLY CLARK - HUGGIES" → "KIMBERLY CLARK". Solo se usa si la familia existe del otro lado. */
export function clientFamily(name: string): string {
  const i = name.indexOf(' - ');
  return i > 0 ? name.slice(0, i).trim() : name.trim();
}

/**
 * Clave de cruce de nombres de cliente: mayúsculas y espacios colapsados. No saca tildes a
 * propósito (no es foldKey): los nombres vienen en mayúsculas de BigQuery y una tilde distingue
 * clientes. Exportada desde el 26/10/2026: la usa también `shared/org-rules.ts` (Org Service).
 */
export const normClient = (s: string) => s.toUpperCase().replace(/\s+/g, ' ').trim();

export type Quadrant = 'mal_margen_mala_prod' | 'mal_margen_buena_prod' | 'buen_margen_mala_prod' | 'sano';

export const QUADRANT_LABEL: Record<Quadrant, string> = {
  mal_margen_mala_prod: 'Mal margen + baja productividad (problema doble: primero productividad y pricing)',
  mal_margen_buena_prod: 'Mal margen + buena productividad (probable tema de pricing)',
  buen_margen_mala_prod: 'Buen margen + baja productividad (igual hay que atacarla)',
  sano: 'Buen margen + buena productividad (sanos)',
};

export interface MatrixClient {
  client: string;
  pod: string;
  pi: number | null;
  gapPct: number | null;
  status: PiStatus;
  revenue: number;
  gmPct: number;
  quadrant: Quadrant;
  /** Sub-dotado: cuenta como "buena productividad" pero con bandera de riesgo de delivery. */
  deliveryRisk: boolean;
  hasEstimado: boolean;
}

export interface MatrixView {
  /** Corte de "buen margen": GM% ponderado por revenue de los clientes con margen confiable. */
  cutoffGm: number | null;
  quadrants: Record<Quadrant, MatrixClient[]>;
  /** Costo 0 o GM ≥ 95 %: costo de creadores sin cargar. Solo revenue. */
  unreliable: Array<{ client: string; revenue: number; pi: number | null }>;
  /** Menos de 2 roles con costo: se deduciría un sueldo. Solo revenue. */
  confidential: Array<{ client: string; revenue: number; pi: number | null }>;
  /** Con snapshot pero sin fila en Capacity_Final (ni por familia). */
  unmatched: string[];
  /** Con snapshot pero sin need: no tienen PI. */
  withoutNeed: string[];
}

/** Suma varias filas de Capacity_Final en una (un cliente de Replit partido en business units). */
function mergeMargins(client: string, rows: MarginRow[]): MarginRow {
  return {
    client,
    revenue: rows.reduce((s, r) => s + r.revenue, 0),
    cost: rows.reduce((s, r) => s + r.cost, 0),
    rolesWithCost: Math.max(...rows.map((r) => r.rolesWithCost)),
  };
}

/**
 * Cruza PI (CP, mes cerrado) con revenue y margen de Capacity_Final del mismo mes. Orden del
 * match: nombre exacto → crosswalk (client_name_crosswalk) → familia ("X - Y" → "X").
 */
export function computeMatrix(pis: ClientPi[], margins: MarginRow[], crosswalk: CrosswalkRow[] = []): MatrixView {
  const byName = new Map<string, MarginRow>();
  for (const m of margins) byName.set(normClient(m.client), m);
  const xw = new Map<string, string[]>();
  for (const c of crosswalk) xw.set(normClient(c.replit), [...(xw.get(normClient(c.replit)) ?? []), normClient(c.capacity)]);
  const find = (client: string): MarginRow | undefined => {
    const exact = byName.get(normClient(client));
    if (exact) return exact;
    const viaXw = (xw.get(normClient(client)) ?? []).map((n) => byName.get(n)).filter((r): r is MarginRow => !!r);
    if (viaXw.length) return mergeMargins(viaXw.map((r) => r.client).join(' + '), viaXw);
    return byName.get(normClient(clientFamily(client)));
  };

  // Varias filas de snapshot pueden caer en la misma familia (KIMBERLY CLARK - HUGGIES/KOTEX/PLENITUD):
  // el margen es de la familia, así que se agregan los FTE y el need y se cruzan una sola vez.
  const grouped = new Map<string, { clients: ClientPi[]; margin: MarginRow }>();
  const unmatched: string[] = [];
  const withoutNeed: string[] = [];
  for (const p of pis.filter((x) => x.model === 'CP')) {
    if (p.need === null) {
      withoutNeed.push(p.client);
      continue;
    }
    const m = find(p.client);
    if (!m) {
      unmatched.push(p.client);
      continue;
    }
    const g = grouped.get(m.client) ?? { clients: [], margin: m };
    g.clients.push(p);
    grouped.set(m.client, g);
  }

  const reliable = (m: MarginRow) => m.rolesWithCost >= 2 && m.cost > 0 && m.revenue > 0 && (m.revenue - m.cost) / m.revenue < 0.95;
  const reliableRows = [...grouped.values()].map((g) => g.margin).filter(reliable);
  const rev = reliableRows.reduce((s, m) => s + m.revenue, 0);
  const cost = reliableRows.reduce((s, m) => s + m.cost, 0);
  const cutoffGm = rev > 0 ? (rev - cost) / rev : null;

  const quadrants: Record<Quadrant, MatrixClient[]> = { mal_margen_mala_prod: [], mal_margen_buena_prod: [], buen_margen_mala_prod: [], sano: [] };
  const unreliable: MatrixView['unreliable'] = [];
  const confidential: MatrixView['confidential'] = [];
  for (const [family, g] of grouped) {
    const fte = g.clients.reduce((s, c) => s + c.fte, 0);
    const need = g.clients.reduce((s, c) => s + (c.need ?? 0), 0);
    const pi = fte > 0 ? need / fte : null;
    const gapPct = need > 0 ? (fte - need) / need : null;
    const m = g.margin;
    // Primero el costo sin cargar (costo 0 / GM ≥ 95 %): ahí "0 roles con costo" es consecuencia, no confidencialidad.
    if (!(m.cost > 0) || (m.revenue > 0 && (m.revenue - m.cost) / m.revenue >= 0.95)) {
      unreliable.push({ client: family, revenue: m.revenue, pi });
      continue;
    }
    if (m.rolesWithCost < 2) {
      confidential.push({ client: family, revenue: m.revenue, pi });
      continue;
    }
    if (!reliable(m)) {
      unreliable.push({ client: family, revenue: m.revenue, pi });
      continue;
    }
    const gmPct = (m.revenue - m.cost) / m.revenue;
    const status = piStatus(gapPct);
    const badProd = status === 'sobre_dotado';
    const goodMargin = cutoffGm !== null && gmPct >= cutoffGm;
    const quadrant: Quadrant = !goodMargin && badProd ? 'mal_margen_mala_prod' : !goodMargin ? 'mal_margen_buena_prod' : badProd ? 'buen_margen_mala_prod' : 'sano';
    quadrants[quadrant].push({
      client: family,
      pod: [...new Set(g.clients.map((c) => c.pod))].join(' / '),
      pi,
      gapPct,
      status,
      revenue: m.revenue,
      gmPct,
      quadrant,
      deliveryRisk: status === 'sub_dotado',
      hasEstimado: g.clients.some((c) => c.hasEstimado),
    });
  }
  for (const q of Object.keys(quadrants) as Quadrant[]) quadrants[q].sort((a, b) => b.revenue - a.revenue);
  unreliable.sort((a, b) => b.revenue - a.revenue);
  confidential.sort((a, b) => b.revenue - a.revenue);
  return { cutoffGm, quadrants, unreliable, confidential, unmatched, withoutNeed };
}

/* ───────────────────────── Render ───────────────────────── */

const n2 = (v: number | null | undefined) => (v === null || v === undefined || !Number.isFinite(v) ? '-' : v.toFixed(2));
const pct0 = fmtPct0;
const ym = (y: number, m: number) => `${monthNameEs(m)} ${y}`;

/** Tabla V1 vs V2 a partir de las filas de parámetros vigentes en BigQuery (no de un texto fijo). */
export function renderModelParams(params: ModelParamRow[]): string {
  const versions = [...new Set(params.map((p) => p.modelVersion))].sort();
  const label = (v: string) => `${modelLabel(v)} (${v})`;
  const rowsFor = (kind: ModelParamRow['kind'], title: string) => {
    const names = [...new Set(params.filter((p) => p.kind === kind).map((p) => p.name))].sort();
    if (!names.length) return '';
    const rows = names.map((name) => [
      name,
      ...versions.map((v) => {
        const p = params.find((x) => x.kind === kind && x.name === name && x.modelVersion === v);
        return p ? `${n2(p.value)}${p.validTo ? ` (hasta ${p.validTo})` : ''}` : '-';
      }),
    ]);
    return `**${title}**\n${mdTable([kind === 'tier' ? 'Tier' : kind === 'asset' ? 'Tipo de asset' : 'Parámetro', ...versions.map(label)], rows)}`;
  };
  return [rowsFor('tier', 'Horas fijas por tier (hs/mes)'), rowsFor('asset', 'Horas por asset (hs/asset)'), rowsFor('time', 'Horas efectivas por persona (hs/mes)')].filter(Boolean).join('\n\n');
}

export function renderContentProductivityDigest(x: ContentProductivityInput): ContentProductivitySections {
  const monthLbl = ym(x.year, x.month);
  const cp = x.snapshots.filter((r) => r.model === 'CP');
  const cs = x.snapshots.filter((r) => r.model === 'CS');
  const cpVersion = [...new Set(cp.map((r) => r.modelVersion))].join(', ') || '-';
  const csVersion = [...new Set(cs.map((r) => r.modelVersion))].join(', ') || '-';
  const estimados = cp.filter((r) => r.hasEstimado && !isNonClient(r.client)).length;
  const clients = computeClientPi(x.snapshots);
  const podsCp = computePodPi(x.snapshots, 'CP');
  const podsCs = computePodPi(x.snapshots, 'CS');
  const open = computeOpenCapacity(x.allocations);
  const matrix = computeMatrix(clients, x.margins, x.crosswalk ?? []);

  const dataLine = `_Datos del cierre de **${monthLbl}** (snapshot real de Replit, modelo ${modelLabel(cpVersion)} para CP y ${modelLabel(csVersion)} para CS; ${estimados} clientes CP con algún parámetro estimado). Digest generado el ${x.today}._`;

  // ── Resumen (siempre en el contexto, chico)
  const totalCp = podsCp.reduce((a, p) => ({ need: a.need + p.need, fte: a.fte + p.fte }), { need: 0, fte: 0 });
  const summary = [
    `# ${CONTENT_SECTION_TITLE.summary}`,
    dataLine,
    mdFields([
      ['PI ponderado CP (Content Producer)', totalCp.fte > 0 ? n2(totalCp.need / totalCp.fte) : '-'],
      ['Clientes CP con PI', String(clients.filter((c) => c.model === 'CP' && c.pi !== null).length)],
      ['Sobre-dotados CP (gap > +20 %)', String(clients.filter((c) => c.model === 'CP' && c.status === 'sobre_dotado').length)],
      ['Sub-dotados CP (gap < −20 %)', String(clients.filter((c) => c.model === 'CP' && c.status === 'sub_dotado').length)],
      ['Open capacity total (FTE)', `${open.totals.openCapacity.toFixed(1)} de ${open.totals.fteTotal.toFixed(1)} (PTO ${open.totals.pto.toFixed(1)} · slack ${open.totals.slack.toFixed(1)} · trainings ${open.totals.trainings.toFixed(1)} · otros proyectos ${open.totals.otherProjects.toFixed(1)}) · NOT ALLOCATED ${open.totals.notAllocated.toFixed(1)} aparte`],
      ['Corte de buen margen (GM % ponderado, clientes con margen confiable)', pct0(matrix.cutoffGm)],
      ['Carga del mes', (x.leadLoad ?? []).length ? `${(x.leadLoad ?? []).reduce((s, l) => s + l.noLoad + l.incomplete + l.exceeded, 0)} de ${(x.leadLoad ?? []).reduce((s, l) => s + l.people, 0)} personas sin 100 %` : '-'],
      ['Assets CP reales del mes', String((x.assets ?? []).filter((a) => a.source === 'CP' && !a.isForecast).reduce((s, a) => s + a.qty, 0) || '-')],
    ]),
    `Secciones disponibles: ${(Object.keys(CONTENT_SECTION_TITLE) as ContentSection[]).filter((s) => s !== 'summary').map((s) => CONTENT_SECTION_TITLE[s]).join(' · ')}.`,
  ].join('\n\n');

  // ── Modelo
  const model = [
    `# ${CONTENT_SECTION_TITLE.model}`,
    `**PI (Productivity Index) = need ÷ FTE real = 1 / (1 + gap_pct).** PI = 1 es el estándar; PI > 1 se usó menos gente que el estándar; PI < 1 se usó más. Semáforo sobre gap_pct: > +20 % sobre-dotado / baja productividad (PI < 0,83; chequear antes que no sea assets sin cargar); < −20 % sub-dotado (PI > 1,25: riesgo de delivery o burnout, no "bueno" sin mirar entregas); entre ±20 % dentro de estándar; −100 % = hay assets y nadie asignado. \`has_estimado\` = algún parámetro estimado (UGC, AI video, Influencers): tomar el gap con cautela. Más videos por operator no es más productividad: depende del mix (estático 0,3 hs vs UGC o Advocacy 1,8 hs).`,
    `**Tres modelos.** CP Same Store: need = horas fijas del tier del cliente + Σ(assets × horas por tipo de asset), ÷ horas efectivas por persona (20 días × 8 hs × (1 − 10 % de slack) = 144 hs/mes; el 10 % es una convención: industria ~8–10 %, call centers hasta 20 %). Los FTE reales se convierten a equivalentes Senior Analyst por rol (no comparar con la suma cruda de FTE). CS: snapshots con tier_cs, cs_mix y cs_todo_interno; el need no viene explícito (se recupera como fte − gap_q); hay poco volumen de assets de calidad porque falta distinguir creaciones/adaptaciones y crowd/interno de forma consistente, y el equipo no creció en el año gracias a más uso de crowd. Partnerships: need = Σ(packs del mes × FTE estándar por pack), con el estándar por partner y pack (lead, operador, CS, total); TikTok (Paquete P/M, Flex, UGC, REMIX, Symphony, CUSTOM) y Meta (KITS, MPA); Google es ad hoc.`,
    `**V1 → V2 de CP Same Store.** V2 (v2_2026Q4) rige desde el 1-oct-2026 y está en Replit y en la Pricing Tool. Lectura: UGC y Advocacy quedan con el mismo peso ("video con creador"); animado sube porque estaba subestimado; el fijo baja para no cargar de entrada a un cliente nuevo con 2–3 assets; la complejidad se simplifica (todos Low salvo los "whoppers"). **Los snapshots no se recalculan**: todos los cierres hasta septiembre 2026 y el forecast congelado el 14-oct son V1; el primer snapshot V2 es el cierre de octubre (se congela ~14-nov). Al comparar septiembre contra octubre en adelante, el gap no es 100 % comparable por el cambio de modelo; el efecto de V2 NO es una mejora de productividad.`,
    `Valores vigentes en BigQuery (Capacity_CP_Model_US; la copia Capacity_CP_Model en us-central1 es la que lee Replit — ⚠️ confirmar que siguen sincronizadas):`,
    renderModelParams(x.params),
    `⚠️ Diferencias con el borrador del 21-oct a confirmar con Operaciones: en BigQuery Influencers V2 figura con 0 hs/asset (el borrador decía "sin cambios") y V2 trae los tiers AdHoc y Standard además de Low/Mid/High. El histórico de tiers (client_tier_history) solo tiene filas V1: qué clientes quedan como whopper en V2 está pendiente.`,
  ].join('\n\n');

  // ── PI por cliente
  const clientTable = (m: ContentModel) =>
    mdTable(
      ['Cliente', 'Pod', 'FTE real (SA eq)', 'Need (SA eq)', 'PI', 'gap %', 'Estado', 'Estimado'],
      clients.filter((c) => c.model === m).map((c) => [c.client, c.pod, n2(c.fte), n2(c.need), n2(c.pi), pct0(c.gapPct), PI_STATUS_LABEL[c.status], c.hasEstimado ? 'sí' : 'no']),
      { maxRows: 80 }
    );
  const clientsSection = [
    `# ${CONTENT_SECTION_TITLE.clients}`,
    dataLine,
    `## CP (Content Producer) — modelo ${modelLabel(cpVersion)}`,
    clientTable('CP') || '_Sin snapshot CP para el mes._',
    `## CS (Customer Specialist) — modelo ${modelLabel(csVersion)}`,
    cs.length ? `_El PI de CS tiene menos volumen de assets de calidad detrás (ver Modelo): gaps muy altos suelen ser assets sin cargar más que gente de más. Complementar con revenue por headcount._\n\n${clientTable('CS')}` : '_Sin snapshot CS para el mes._',
    `Los no-clientes de los snapshots (SLACK - *, CAPACITY - *, PTO/OOO, TRAININGS, OTHER PROJECTS, NOT ALLOCATED) quedan fuera del PI. El PI de un pod mide al equipo que ese líder gestiona, no a la persona. Mirar tendencia o trimestre, no un mes aislado.`,
  ].join('\n\n');

  // ── PI por pod
  const podTable = (rows: PodPi[]) =>
    mdTable(
      ['Pod', 'Clientes con need', 'Need (SA eq)', 'FTE real (SA eq)', 'PI ponderado', 'Sobre-dotados', 'Sub-dotados'],
      rows.map((p) => [p.pod, p.clients, n2(p.need), n2(p.fte), n2(p.pi), p.sobreDotados, p.subDotados])
    );
  const pods = [
    `# ${CONTENT_SECTION_TITLE.pods}`,
    dataLine,
    `## CP — modelo ${modelLabel(cpVersion)}`,
    podTable(podsCp) || '_Sin datos._',
    podsCs.length ? `## CS — modelo ${modelLabel(csVersion)}\n\n${podTable(podsCs)}` : '',
    `El PI ponderado es Σneed ÷ ΣFTE del pod (no el promedio de los PI). Un pod con pocos clientes (US) mueve el número con un solo caso. Nunca sugerir bajar el estándar de un pod o cliente: los desvíos se explican (retrabajo del cliente, búsqueda de creadores, equipos con gente nueva) y, si la causa es el cliente, se escala con Growth Partner; no se agrega capacity.`,
  ].filter(Boolean).join('\n\n');

  // ── Open capacity
  const openSection = [
    `# ${CONTENT_SECTION_TITLE.openCapacity}`,
    `_Allocations de **${monthLbl}** por líder directo (manager del mes, con overrides). Open capacity = SLACK - * + PTO/OOO + TRAININGS + OTHER PROJECTS. NOT ALLOCATED va aparte (no es open capacity por definición). Las bolsas CAPACITY - * son de Partnerships: ${open.capacityPools.toFixed(1)} FTE en total, no cuentan._`,
    mdTable(
      ['Líder', 'FTE total', 'Open capacity', '% del equipo', 'Slack', 'PTO/OOO', 'Trainings', 'Other projects', 'Not allocated'],
      open.leads.map((l) => [l.lead, l.fteTotal.toFixed(1), l.openCapacity.toFixed(1), pct0(l.openPct), l.slack.toFixed(1), l.pto.toFixed(1), l.trainings.toFixed(1), l.otherProjects.toFixed(1), l.notAllocated.toFixed(1)]),
      { maxRows: 60 }
    ) || '_Sin allocations para el mes._',
    `Totales: open capacity ${open.totals.openCapacity.toFixed(1)} FTE de ${open.totals.fteTotal.toFixed(1)} (${pct0(open.totals.fteTotal > 0 ? open.totals.openCapacity / open.totals.fteTotal : null)}): PTO/OOO ${open.totals.pto.toFixed(1)} · slack ${open.totals.slack.toFixed(1)} · trainings ${open.totals.trainings.toFixed(1)} · other projects ${open.totals.otherProjects.toFixed(1)}; NOT ALLOCATED ${open.totals.notAllocated.toFixed(1)}. No se mezcla con el PI: un equipo puede tener buen PI y mucha open capacity (es costo). Para un Head, sumar los líderes de su árbol. Qué número aprobar y cuándo se decide en la reunión de capacity.`,
  ].join('\n\n');

  // ── Matriz
  const qTable = (rows: MatrixClient[]) =>
    mdTable(
      ['Cliente (familia)', 'Pod', 'Revenue', 'GM %', 'PI', 'gap %', 'Estado', 'Riesgo delivery', 'Estimado'],
      rows.map((c) => [c.client, c.pod, fmtK(c.revenue), pct0(c.gmPct), n2(c.pi), pct0(c.gapPct), PI_STATUS_LABEL[c.status], c.deliveryRisk ? 'sí' : 'no', c.hasEstimado ? 'sí' : 'no']),
      { maxRows: 12 }
    );
  const quadrantBlocks = (Object.keys(QUADRANT_LABEL) as Quadrant[]).map((q) => {
    const rows = matrix.quadrants[q];
    const rev = rows.reduce((s, r) => s + r.revenue, 0);
    return `## ${QUADRANT_LABEL[q]} — ${rows.length} clientes · revenue ${fmtK(rev)}\n\n${qTable(rows) || '_Ninguno._'}`;
  });
  const matrixSection = [
    `# ${CONTENT_SECTION_TITLE.matrix}`,
    `_Cruce del PI de CP (snapshot de **${monthLbl}**) con revenue y margen de Capacity_Final (Actual, PracticeName = Content, mismo mes), por familia de cliente (Capacity_Final conserva el nombre madre: KIMBERLY CLARK - HUGGIES/KOTEX/PLENITUD → KIMBERLY CLARK). Eje productividad: mala = gap > +20 %; buena = dentro de ±20 % o sub-dotado (con bandera de riesgo de delivery). Eje margen: buen margen = GM % ≥ **${pct0(matrix.cutoffGm)}**, el GM % ponderado por revenue de los clientes con margen confiable en este mes (se recalcula en cada corrida)._`,
    ...quadrantBlocks,
    `## Margen no confiable (costo 0 o GM ≥ 95 %: costo de creadores todavía sin cargar) — ${matrix.unreliable.length}\n\n${mdTable(['Cliente', 'Revenue', 'PI'], matrix.unreliable.map((u) => [u.client, fmtK(u.revenue), n2(u.pi)]), { maxRows: 15 }) || '_Ninguno._'}\n\nHay un desfase entre el revenue reconocido y la entrada de costos de creadores: un GM muy alto normalmente es costo sin cargar, no rentabilidad real. Estos clientes no se ubican en cuadrantes.`,
    `## Otros clientes (menos de 2 roles con costo: el margen no se muestra por confidencialidad) — ${matrix.confidential.length}\n\n${mdTable(['Cliente', 'Revenue', 'PI'], matrix.confidential.map((u) => [u.client, fmtK(u.revenue), n2(u.pi)]), { maxRows: 20 }) || '_Ninguno._'}`,
    `## Fuera de la matriz\n- Sin need (sin assets cargados, no tienen PI): ${matrix.withoutNeed.join(', ') || 'ninguno'}.\n- Con snapshot pero sin fila en Capacity_Final del mes (ni por familia): ${matrix.unmatched.join(', ') || 'ninguno'}.`,
  ].join('\n\n');

  // ── Partnerships
  const packsByPartner = [...new Set(x.packs.map((p) => p.partner))].sort();
  const partnershipsSection = [
    `# ${CONTENT_SECTION_TITLE.partnerships}`,
    `**Need de Partnerships = Σ(packs del mes × FTE estándar por pack).** Estándar vigente por partner y pack (FTE por pack; sin costos):`,
    ...packsByPartner.map(
      (partner) =>
        `## ${partner}\n\n${mdTable(
          ['Pack', 'Lead (FTE)', 'Operador (FTE)', 'CS (FTE)', 'Total (FTE)'],
          x.packs.filter((p) => p.partner === partner).map((p) => [p.pack, n2(p.lead), n2(p.operator), n2(p.cs), n2(p.total)])
        )}`
    ),
    `**Coeficiente por región.** Andinos va ×0,6 **sobre el need** (no sobre los packs): tardan más en procesar un pack, así que el need del mes baja. Evidencia: mediana de entrega SoCo ≈ 33 días vs Andinos ≈ 56 → 33/56 ≈ 0,59. Las regiones con volumen y ~30 días de mediana siguen con el estándar sin ajuste; Google y Meta se tratan ad hoc. ⚠️ El 0,6 no vive en ninguna tabla (partnerships_forecast.pod_mediana tiene un valor plano para todos los pods; partnership_package_std solo trae precios por región): es una regla vigente del modelo, no un dato consultable. México tiene la mediana ~35 % por encima de SoCo y no tiene ajuste definido: es una observación, no un coeficiente.`,
    `## Medianas de entrega observadas (proyectos cerrados con fecha de inicio y fin)\n\nLa clasificación de países en regiones es una aproximación de este análisis (SoCo = AR/CL/UY/PY/BO; Andinos = CO/PE/EC; México/CAM = MX/Centroamérica; Brasil; USA).\n\n${mdTable(['Región', 'Proyectos', 'Mediana (días)'], x.mediansByRegion.map((m) => [m.group, m.projects, m.medianDays])) || '_Sin datos._'}\n\n${mdTable(['Plataforma (client_type)', 'Proyectos', 'Mediana (días)'], x.mediansByPlatform.map((m) => [m.group, m.projects, m.medianDays])) || '_Sin datos._'}`,
  ].join('\n\n');

  // ── Forecast (proyectado)
  const fcMonths = [...new Set(x.forecasts.map((f) => `${f.forecastYear}-${String(f.forecastMonth).padStart(2, '0')}`))].sort();
  const fcBlocks = fcMonths.map((key) => {
    const [fy, fm] = key.split('-').map(Number);
    const rows = x.forecasts.filter((f) => f.forecastYear === fy && f.forecastMonth === fm);
    const pods = computePodPi(rows.map((f) => ({ model: 'CP' as const, client: f.client, pod: f.pod, year: fy, month: fm, fte: f.fte, gapQ: f.gapQ, gapPct: f.gapPct, modelVersion: f.modelVersion, hasEstimado: false })), 'CP');
    const version = [...new Set(rows.map((r) => r.modelVersion))].join(', ');
    return `## ${ym(fy, fm)} — proyectado (modelo ${modelLabel(version)})\n\n${podTable(pods) || '_Sin filas._'}`;
  });
  const forecastSection = [
    `# ${CONTENT_SECTION_TITLE.forecast}`,
    x.forecasts.length
      ? `_Forecast congelado al cierre de **${ym(x.forecasts[0].actualYear, x.forecasts[0].actualMonth)}** (cp_forecast_snapshots). Es **proyectado**, no real: el need sale de los assets previstos y los FTE de las allocations planificadas._`
      : '_No hay forecast congelado para el último cierre._',
    ...fcBlocks,
  ].join('\n\n');

  // ── Completitud de carga por líder (bloque A del skill original; conteos, sin nombres de personas)
  const leadLoad = x.leadLoad ?? [];
  const totPeople = leadLoad.reduce((s, l) => s + l.people, 0);
  const totMissing = leadLoad.reduce((s, l) => s + l.noLoad + l.incomplete + l.exceeded, 0);
  const missingPct = totPeople > 0 ? totMissing / totPeople : 0;
  const loadLight = totMissing === 0 ? '🟢 todo cargado' : missingPct > 0.1 ? '🔴 faltantes relevantes (más del 10 % de la gente)' : '🟡 faltan pocos ítems';
  const loadSection = [
    `# ${CONTENT_SECTION_TITLE.load}`,
    `_Allocations de **${monthLbl}** (cada persona debería sumar 100 % en el mes). Conteos por líder directo; sin nombres de personas: para saber quién, el líder lo ve en Replit._`,
    leadLoad.length
      ? `${loadLight}: ${totMissing} de ${totPeople} personas con carga faltante o distinta de 100 % · ${x.locksCount ?? '-'} clientes con la carga cerrada (lock) en el mes.\n\n${mdTable(
          ['Líder', 'Personas', 'Con 100 %', 'Sin carga', 'Incompleta (< 100 %)', 'Excedida (> 100 %)'],
          [...leadLoad].sort((a, b) => b.noLoad + b.incomplete + b.exceeded - (a.noLoad + a.incomplete + a.exceeded) || b.people - a.people).map((l) => [l.lead ?? '(sin manager)', l.people, l.ok, l.noLoad, l.incomplete, l.exceeded]),
          { maxRows: 60 }
        )}`
      : '_Sin datos de carga para el mes._',
    `**Clientes de Content con horas y sin assets ni packs cargados** (${(x.hoursWithoutAssets ?? []).length}): ${(x.hoursWithoutAssets ?? []).slice(0, 25).join(', ') || 'ninguno'}${(x.hoursWithoutAssets ?? []).length > 25 ? '…' : ''}. Un "sobre-dotado" en uno de estos suele ser carga incompleta, no gente de más: chequearlo antes de concluir. Varias personas "sin carga" bajo un mismo líder suele ser que ese líder no cargó todavía (no que la gente se fue).`,
    // Los snapshots no se recalculan: si hoy hay assets cargados para un cliente que quedó "sin need", se congeló antes de la carga.
    (() => {
      const loadedLater = matrix.withoutNeed.filter((c) => (x.clientAssets ?? []).some((a) => normClient(a.client) === normClient(c) && a.qty > 0));
      return loadedLater.length ? `**Sin need en el snapshot pero con assets cargados hoy** (${loadedLater.length}): ${loadedLater.join(', ')}. El snapshot se congeló antes de que se cargaran los assets: el PI de esos clientes no existe para este mes y no es un problema de productividad.` : '';
    })(),
  ].filter(Boolean).join('\n\n');

  // ── Assets producidos (bloque C): CP por tipo con MoM, CS por modo × interno/crowd, packs por partner
  const assets = x.assets ?? [];
  const assetTable = (source: AssetTypeRow['source'], label: string) => {
    const rows = assets.filter((a) => a.source === source);
    if (!rows.length) return '';
    const real = rows.filter((a) => !a.isForecast).sort((a, b) => b.qty - a.qty);
    const fc = rows.filter((a) => a.isForecast);
    const fcOf = (l: string) => fc.find((f) => f.label === l)?.qty;
    const hasFc = fc.length > 0;
    return `## ${source}\n\n${mdTable(
      [label, 'Real del mes', 'Mes anterior (real)', 'Var.', ...(hasFc ? ['Forecast del mes'] : []), 'Clientes'],
      real.map((a) => [a.label, a.qty, a.prevQty ?? '-', (() => { const d = a.prevQty === null ? null : pctDelta(a.qty, a.prevQty); return d === null ? '-' : `${d >= 0 ? '+' : ''}${Math.round(d)} %`; })(), ...(hasFc ? [fcOf(a.label) ?? '-'] : []), a.clients])
    )}`;
  };
  const fteByClient = new Map(clients.filter((c) => c.model === 'CP').map((c) => [normClient(c.client), c.fte]));
  const perFte = (x.clientAssets ?? [])
    .map((a) => ({ client: a.client, qty: a.qty, fte: fteByClient.get(normClient(a.client)) ?? null }))
    .filter((r) => r.fte !== null && r.fte > 0)
    .map((r) => ({ ...r, ratio: r.qty / (r.fte as number) }))
    .sort((a, b) => b.ratio - a.ratio);
  const assetsSection = [
    `# ${CONTENT_SECTION_TITLE.assets}`,
    `_Assets cargados en Replit para **${monthLbl}** (CP: client_assets por tipo; CS: cs_client_assets por modo creación/adaptación e interno/crowdsource, real vs forecast; Partnerships: packs por partner, sin la fila __revenue__). "Mes anterior" compara contra el mes previo real._`,
    assetTable('CP', 'Tipo de asset') || '_Sin assets CP cargados._',
    assetTable('CS', 'Modo · trabajo') || '',
    assetTable('Partnerships', 'Partner') || '',
    `## Assets por FTE (CP, solo clientes con horas y assets en el mes)\n\n_Más videos por operator no es más productividad: depende del mix (un estático son 0,3 hs; un UGC o Advocacy 1,8 hs). ~100 videos por operator hacia fin de 2027 es aspiracional, no target._\n\n${mdTable(['Cliente', 'Assets (real)', 'FTE real (SA eq)', 'Assets por FTE'], perFte.map((r) => [r.client, r.qty, n2(r.fte), r.ratio.toFixed(1)]), { maxRows: 25 }) || '_Sin cruce disponible._'}`,
  ].filter(Boolean).join('\n\n');

  // ── Reglas y faltantes
  const assumptions = [
    `**Reglas de lectura.** El estándar es uniforme para todos los clientes: no se modifica para arreglar un desvío y nunca se sugiere bajarlo. Causas típicas de baja productividad: retrabajo del cliente (cambios de brief recurrentes), gestión y búsqueda de creadores, equipos con mucha gente nueva. Si la causa es el cliente, se escala con Growth Partner; sobre-staffear por ineficiencia es una decisión consensuada y justificada. Todo pedido de personal se contrasta contra el estándar, la productividad del pod completo y su open capacity. Mirar tendencia o trimestre, no un mes aislado. Si la explicación de un desvío no está en los datos, decirlo y derivar al manager del pod o a Operaciones. Siempre decir mes, modelo (V1/V2) y si el dato es real (snapshot) o proyectado (forecast).`,
    `**Confidencialidad.** Nunca costo por persona, por rol ni costo promedio de rol (ni cost_avg de los packs ni cwsc_month_cost por persona): este digest no trae ninguno. Revenue y margen solo a nivel cliente y solo con ≥ 2 roles con costo. No hay mapeo de quién pregunta a su equipo: si piden "mi equipo", pedir el nombre del líder. Si preguntan cuánto cuesta un rol o una persona: rechazar y ofrecer la vista agregada.`,
    `**Metas.** ~100 videos por operator hacia fin de 2027 es aspiracional, no target (hay quien propone 150): equivale a 5–6 marcas por operator. Lo que se gana en productividad hoy tiende a ir a precio. Las cifras de reuniones (Q3) son una foto: vale lo que dice BigQuery hoy.`,
    `**Glosario.** CP: Content Producer. CS: Customer Specialist. SA eq: equivalente Senior Analyst. PI: Productivity Index. Whoppers: casos puntuales de complejidad alta. Pod: equipo regional. Growth Partner: equipo de Business que conversa con el cliente.`,
    `**Datos faltantes en este digest.** Clientes CP sin need: ${matrix.withoutNeed.length}. Clientes con snapshot sin fila en Capacity_Final: ${matrix.unmatched.length}. Clientes con margen no confiable: ${matrix.unreliable.length}; en "Otros clientes" por confidencialidad: ${matrix.confidential.length}. Clientes CP con parámetros estimados: ${estimados}. Fuera de alcance por decisión: benchmark de mercado, tracker de horas de reuniones, identificación de quién pregunta; la tabla del coeficiente regional de Partnerships no existe en BigQuery.`,
  ].join('\n\n');

  return { summary, model, clients: clientsSection, pods, openCapacity: openSection, matrix: matrixSection, partnerships: partnershipsSection, forecast: forecastSection, load: loadSection, assets: assetsSection, assumptions };
}
